import { debounce } from "lodash";
import * as Sentry from "@sentry/react";
import { createMetricsClient, METRICS_PROVIDERS } from "au.com.easil.metrics";
import Logger from "lib/logger";
import { getPath } from "lib/lodash";
import PATHS from "routes/paths";

const METRICS_PROVIDER =
  process.env.REACT_APP_METRICS_PROVIDER || METRICS_PROVIDERS.SEGMENT;

const usingSegment = METRICS_PROVIDER === METRICS_PROVIDERS.SEGMENT;
const PAGE_VIEW_EVENT = "page_view";

const userTracker = {};

/* the most recently identified user; lets us attach team_id / user id to every
   event regardless of the active provider */
let identifiedUser = null;

let metricsClient = null;
const getMetricsClient = () => {
  if (!metricsClient) {
    metricsClient = createMetricsClient({
      provider: METRICS_PROVIDER,
      segment: typeof window !== "undefined" ? window.analytics : undefined,
      sentry: Sentry
    });
  }
  return metricsClient;
};

const safelyCall = function (f) {
  return function () {
    try {
      f.apply(this, arguments);
    } catch (error) {
      /* in case 3rd party throws something, we log and keep going */
      Logger.error(error);
      Logger.warn("3rd Party metrics operation failed, logging and continuing");
    }
  };
};

/* eslint-disable */
// prettier-ignore
function loadSegmentSnippet () {
  !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on"];analytics.factory=function(t){return function(){var e=Array.prototype.slice.call(arguments);e.unshift(t);analytics.push(e);return analytics}};for(var t=0;t<analytics.methods.length;t++){var e=analytics.methods[t];analytics[e]=analytics.factory(e)}analytics.load=function(t,e){var n=document.createElement("script");n.type="text/javascript";n.async=!0;n.src="https://cdn.segment.com/analytics.js/v1/"+t+"/analytics.min.js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(n,a);analytics._loadOptions=e};analytics.SNIPPET_VERSION="4.1.0";
    analytics.load(process.env.REACT_APP_SEGMENT_APP_KEY);
    analytics.page();
  }}();
}
/* eslint-enable */

function initialize() {
  /* Sentry is initialised once in lib/sentry; the Segment snippet only needs
     loading when Segment is the active provider */
  if (usingSegment) {
    loadSegmentSnippet();
  }
}
userTracker.initialize = safelyCall(initialize);

function identify({
  id,
  email,
  name,
  country,
  roles,
  createdAt: created_at,
  teamPlan: subscription_plan,
  teamName: team_name,
  teamId: team_id
}) {
  identifiedUser = { id, team_id };

  getMetricsClient().identify(id, {
    email,
    roles,
    name,
    country,
    created_at,
    subscription_plan,
    team_id,
    team_name
  });
}
userTracker.identify = safelyCall(identify);

const getPageName = path => {
  if (PATHS.isEditorPath(path)) {
    return "Editor";
  }
  if (PATHS.isOrderPath(path)) {
    return "Orders";
  }
  if (PATHS.isPurchaseCollection(path)) {
    return "Purchase Collection";
  }
  if (PATHS.isInvitationPath(path)) {
    return "Invitation";
  }
  return path;
};

function page({ path, referrer, search, title, url }) {
  const pageName = getPageName(path);

  if (usingSegment) {
    if (!window.analytics) {
      return;
    }
    window.analytics.page(pageName, { path, referrer, search, title, url });
    return;
  }

  const userId = identifiedUser ? identifiedUser.id : null;
  getMetricsClient().track(userId, PAGE_VIEW_EVENT, { page_name: pageName });
}
/* the debounce here avoid tracking redirects */
userTracker.page = debounce(safelyCall(page), 300);

function getTeamId() {
  /* Segment keeps team_id on its identified user; fall back to the user we
     identified ourselves (also the source of truth under Sentry) */
  if (usingSegment && getPath(window, "analytics.user")) {
    return window.analytics.user().traits().team_id;
  }
  return identifiedUser ? identifiedUser.team_id : undefined;
}

function track({ event, properties }) {
  if (usingSegment) {
    // ensure the Segment snippet loaded and isn't getting blocked
    if (!window.analytics || !getPath(window, "analytics.user")) {
      Logger.warn(
        "3rd Party `analytics.user` could not be loaded, aborted tracking call"
      );
      return;
    }
  }

  const propertiesWithTeamId = {
    ...properties,
    team_id: getTeamId()
  };

  const userId = identifiedUser ? identifiedUser.id : null;
  getMetricsClient().track(userId, event, propertiesWithTeamId);
}
userTracker.track = safelyCall(track);

userTracker.start = user => {
  userTracker.initialize();
  userTracker.identify(user);
};

userTracker.events = {
  addGroup: "add_group",
  addPage: "add_page",
  categoryView: "category_view",
  collectionProductAdded: "collection_product_added",
  copyDesign: "copy_design",
  designCreated: "design_created",
  designDownload: "design_download",
  designMerge: "design_merge",
  designRender: "design_render",
  editorCrash: "editor_crash",
  mediaFavourited: "media_favourited",
  mediaUpload: "media_upload",
  pageView: PAGE_VIEW_EVENT,
  purchaseCollectionView: "purchase_collection_view",
  resizeDesign: "resize_design",
  teamMediaUpload: "team_media_upload",
  textMask: "text_mask",
  userMediaUpload: "user_media_upload"
};

export default userTracker;
